import React, { useState, useRef } from "react";
import {
  MdEdit,
  MdFileDownload,
  MdInsertComment,
  MdRemoveCircle,
  MdAddCircle,
} from "react-icons/md";
import { Link, useParams } from "react-router-dom";
import ImageModal from "./components/ImageModal";
import AttachmentPopover from "./components/AttachmentPopover";
import CommentDrawer from "./components/CommentDrawer";
import InvestmentSection from "./components/InvestmentSection";
import { useProofOfInvestmentsApi } from "./hooks/useProofOfInvestmentsApi";
import { useDataTransforms } from "./hooks/useDataTransforms";

const ProofOfInvestments = ({ tdsData, hasDeclarations }) => {
  const { company_employee_id } = useParams();
  const [selectedImage, setSelectedImage] = useState(null);
  const [isImageModalOpen, setIsImageModalOpen] = useState(false);
  const [isCommentDrawerOpen, setIsCommentDrawerOpen] = useState(false);
  const fileInputRef = useRef(null);
  // track open/collapsed state for rental rows (collapsible + / -)
  const [openRentalRows, setOpenRentalRows] = useState({});
  const toggleRentalRow = (idx) => {
    setOpenRentalRows((prev) => ({ ...prev, [idx]: !prev[idx] }));
  };

  // Use custom hooks
  const {
    attachments,
    setAttachments,
    activeAttachmentData,
    setActiveAttachmentData,
    openPopoverKey,
    setOpenPopoverKey,
    activeCommentData,
    setActiveCommentData,
    comments,
    setComments,
    commentText,
    setCommentText,
    isCommentInputFocused,
    setIsCommentInputFocused,
    currentProofListParams,
    setCurrentProofListParams,
    currentCommentListParams,
    setCurrentCommentListParams,
    commentPage,
    setCommentPage,
    hasMoreComments,
    setHasMoreComments,
    uploadDocument,
    deleteProof,
    addComment,
    proofList,
    setProofListParams,
    commentList,
    setCommentListParams,
  } = useProofOfInvestmentsApi();

  const declarationData = tdsData?.data;
  const mainData = declarationData?.main_submission_data;

  const { transformedHouseRent, getStatusDisplay, getCommentDrawerTitles } =
    useDataTransforms(declarationData);

  // If no declarations, show the "no data" state
  if (!hasDeclarations) {
    // Get financial year ID from the API response structure for no declarations
    const financialYearId = tdsData?.data?.financial_years?.financial_year_id;

    return (
      <div className="p-6 mt-6">
        <div className="mb-2">
          <div className="flex items-center gap-2 mb-1">
            <h1 className="text-[18px] font-semibold text-gray-900">
              Proof Of Investments
            </h1>
          </div>
        </div>

        <div className="text-center py-20">
          <div className="mb-6">
            <div className="mx-auto w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mb-4">
              <MdFileDownload className="w-12 h-12 text-gray-400" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              No Declaration Found
            </h3>
            <p className="text-gray-500 mb-6">
              This employee hasn't submitted any investment declaration yet.
            </p>
            <Link
              to={`/employee-details/${company_employee_id}/investments-and-proofs/investment-declaration/${financialYearId}/edit`}
              className="inline-flex items-center px-4 py-2 bg-blue-600 text-white font-medium rounded-md hover:bg-blue-700 transition-colors"
            >
              Submit Declaration
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const taxRegime =
    mainData?.employee_regime_selection?.regime === "old"
      ? "Old Tax Regime"
      : "New Tax Regime";
  const statusDisplay = getStatusDisplay(mainData?.status);

  // Generic handler for file selection
  const handleGenericFileSelect = (attachmentKey, idParams, documentCount) => {
    console.log("handleGenericFileSelect called with:", {
      attachmentKey,
      idParams,
      documentCount,
    });

    if (documentCount === 0) {
      console.log("Opening file explorer");
      setActiveAttachmentData({ attachmentKey, idParams });
      if (fileInputRef.current) {
        fileInputRef.current.click();
      }
    } else {
      console.log("Opening popover and fetching existing proofs");
      setOpenPopoverKey(attachmentKey);
      setCurrentProofListParams(idParams);
      setProofListParams(idParams);
    }
  };

  // Handler for adding more attachments from within the popover
  const handleAddMoreAttachments = (attachmentKey) => {
    console.log("handleAddMoreAttachments for:", attachmentKey);
    setActiveAttachmentData({
      attachmentKey,
      idParams: currentProofListParams,
    });
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleFileChange = (event) => {
    const files = Array.from(event.target.files);
    if (files.length > 0 && activeAttachmentData) {
      const file = files[0];

      console.log("Starting document upload for:", activeAttachmentData);
      uploadDocument.handleAuth.mutate({
        "document[]": file,
        screen: "tds",
      });
    }
    event.target.value = "";
  };

  const handleRemoveAttachment = (key, index) => {
    const currentAttachments = attachments[key] || [];
    const proofToDelete = currentAttachments[index];

    if (proofToDelete?.id) {
      deleteProof.handleAuth.mutate({
        investment_proof_id: proofToDelete.id,
      });
    }
  };

  const openImageModal = (imageUrl) => {
    setSelectedImage(imageUrl);
    setIsImageModalOpen(true);
  };

  // Generic handler for comment click
  const handleGenericCommentClick = (commentKey, idParams) => {
    console.log("handleGenericCommentClick called with:", {
      commentKey,
      idParams,
    });

    setActiveCommentData({ commentKey, idParams });
    setComments([]);
    setCommentPage(0);
    setCurrentCommentListParams({ ...idParams, limit: 10, page_number: 0 });
    setCommentListParams({ ...idParams, limit: 10, page_number: 0 });
    setIsCommentDrawerOpen(true);
  };

  const handleCommentDrawerClose = () => {
    setIsCommentDrawerOpen(false);
    setActiveCommentData(null);
    setComments([]);
    setCurrentCommentListParams(null);
    setCommentPage(0);
    setCommentText("");
    setIsCommentInputFocused(false);
  };

  const handleAddComment = (commentText) => {
    if (!activeCommentData || !commentText.trim()) return;

    const payload = {
      comment: commentText.trim(),
      ...activeCommentData.idParams,
    };

    console.log("Adding comment with payload:", payload);
    addComment.handleAuth.mutate(payload);
  };

  const handleCommentTextChange = (text) => {
    setCommentText(text);
  };

  const handleCommentInputFocus = () => {
    setIsCommentInputFocused(true);
  };

  const handleCommentInputBlur = () => {
    if (!commentText.trim()) {
      setIsCommentInputFocused(false);
    }
  };

  const handleCancelComment = () => {
    setCommentText("");
    setIsCommentInputFocused(false);
  };

  const handleLoadMoreComments = () => {
    const nextPage = commentPage + 1;
    setCommentPage(nextPage);

    const newParams = {
      ...currentCommentListParams,
      page_number: nextPage,
    };
    setCurrentCommentListParams(newParams);
    setCommentListParams(newParams);
  };

  // Compute rental totals (considering regime and actual values)
  const rentalIncomeArray = declarationData?.rental_income || [];
  const regime = mainData?.employee_regime_selection?.regime || "old";
  
  // Calculate declared rental total
  const baseRentalTotal = rentalIncomeArray.reduce(
    (sum, it) => sum + (parseFloat(it.net_income_loss || 0) || 0),
    0
  );
  
  // Calculate actual rental total
  const actualRentalTotal = rentalIncomeArray.reduce((sum, it) => {
    const actualAnnualRent = parseFloat(it.actual_annual_rent_received || 0);
    const actualMunicipalTaxes = parseFloat(it.actual_municipal_taxes_paid || 0);
    const actualNetAnnualValue = actualAnnualRent - actualMunicipalTaxes;
    const actualStandardDeduction = actualNetAnnualValue * 0.3;
    const actualInterest = parseFloat(it.actual_interest_amount || 0);
    
    // Calculate based on regime
    let actualNetIncomeLoss;
    if (regime === "old") {
      actualNetIncomeLoss = actualNetAnnualValue - actualStandardDeduction - actualInterest;
    } else {
      actualNetIncomeLoss = actualNetAnnualValue - actualStandardDeduction;
    }
    
    return sum + actualNetIncomeLoss;
  }, 0);
  
  const mainHomeLoanInterest = parseFloat(
    declarationData?.home_loan?.interest_amount || 0
  );
  const actualMainHomeLoanInterest = parseFloat(
    declarationData?.home_loan?.actual_interest_amount || 0
  );
  
  // For old regime, subtract main home loan interest from total
  const displayedRentalTotal = regime === "old" ? baseRentalTotal - mainHomeLoanInterest : baseRentalTotal;
  const displayedActualRentalTotal = regime === "old" ? actualRentalTotal - actualMainHomeLoanInterest : actualRentalTotal;
  
  const homeLoanInterestDisplay = mainHomeLoanInterest > 0 ? `₹${mainHomeLoanInterest.toLocaleString()}` : null;
  const actualHomeLoanInterestDisplay = actualMainHomeLoanInterest > 0 ? `₹${actualMainHomeLoanInterest.toLocaleString()}` : null;

  return (
    <div className="p-6 mt-6">
      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept="image/*,.pdf,.doc,.docx"
        onChange={handleFileChange}
        className="hidden"
      />

      {/* Modals and Drawers */}
      <ImageModal
        isOpen={isImageModalOpen}
        onClose={() => setIsImageModalOpen(false)}
        imageUrl={selectedImage}
        title="Proof Document"
      />

      <CommentDrawer
        isOpen={isCommentDrawerOpen}
        onClose={handleCommentDrawerClose}
        activeCommentKey={activeCommentData?.commentKey}
        title={
          activeCommentData
            ? getCommentDrawerTitles(activeCommentData.commentKey).title
            : "Comments"
        }
        subtitle={
          activeCommentData
            ? getCommentDrawerTitles(activeCommentData.commentKey).subtitle
            : "Investment Proof Comments"
        }
        comments={comments}
        onAddComment={handleAddComment}
        hasMoreComments={hasMoreComments}
        onLoadMoreComments={handleLoadMoreComments}
        isAddingComment={addComment.isLoading}
        isLoadingComments={commentList?.isLoading}
        commentText={commentText}
        onCommentTextChange={handleCommentTextChange}
        isCommentInputFocused={isCommentInputFocused}
        onCommentInputFocus={handleCommentInputFocus}
        onCommentInputBlur={handleCommentInputBlur}
        onCancelComment={handleCancelComment}
      />

      {/* Header */}
      <div className="mb-2">
        <div className="flex items-center gap-2 mb-1">
          <h1 className="text-[18px] font-semibold text-gray-900">
            Proof Of Investments
          </h1>
          <span className={`text-xs font-medium ${statusDisplay.className}`}>
            {statusDisplay.text}
          </span>
          <button className="text-gray-500 hover:text-gray-700">
            <Link
              to={`/employee-details/${company_employee_id}/investments-and-proofs/proof-of-investments/${mainData?.financial_year?.financial_year_id}/edit`}
            >
              <MdEdit className="w-4 h-4" />
            </Link>
          </button>
        </div>

        <div className="text-gray-700 text-[13px]">
          <span className="font-semibold">Tax Regime :</span>{" "}
          <span className="font-semibold">{taxRegime}</span>
          <Link
            to={`/employee-details/${company_employee_id}/investments-and-proofs/investment-declaration/${mainData?.financial_year?.financial_year_id}/compare-tax-regimes`}
            className="ml-2 text-blue-600 hover:underline text-[12px] font-normal"
          >
            (Change Tax Regime)
          </Link>
        </div>
      </div>

      {/* Table Header */}
      <div className="bg-[#f8f9fb] rounded mt-7 mb-2">
        <div className="flex w-full px-6 py-3">
          <div
            style={{ width: "28%" }}
            className="uppercase text-[13px] font-semibold text-gray-500 tracking-wider"
          >
            PARTICULARS
          </div>
          <div
            style={{ width: "16%" }}
            className="uppercase text-[13px] font-semibold text-gray-500 tracking-wider text-right"
          >
            DECLARED AMOUNT
          </div>
          <div
            style={{ width: "10%" }}
            className="uppercase text-[13px] font-semibold text-gray-500 tracking-wider text-center"
          >
            PROOFS
          </div>
          <div
            style={{ width: "10%" }}
            className="uppercase text-[13px] font-semibold text-gray-500 tracking-wider text-center"
          >
            COMMENTS
          </div>
          <div
            style={{ width: "36%" }}
            className="uppercase text-[13px] font-semibold text-gray-500 tracking-wider text-right"
          >
            ACTUAL AMOUNT
          </div>
        </div>
      </div>

      {/* House Rent Paid Details */}
      {transformedHouseRent.length > 0 && (
        <div className="border-b border-gray-200 pb-4">
          <div className="text-[16px] font-semibold text-gray-900 px-6 pt-6 pb-2">
            House Rent Paid Details
          </div>
          {transformedHouseRent.map((rent, index) => (
            <div
              key={index}
              className={`flex flex-col ${
                index !== transformedHouseRent.length - 1
                  ? "border-b border-gray-100"
                  : ""
              }`}
            >
              <div className="flex w-full px-6 py-2 items-center">
                <div style={{ width: "28%" }}>
                  <div className="text-gray-800 text-[15px]">{rent.period}</div>
                </div>
                <div style={{ width: "16%" }} className="flex justify-end">
                  <div className="text-gray-900 text-[15px] font-medium">
                    {rent.declaredAmount}
                  </div>
                </div>
                <div style={{ width: "10%" }} className="flex justify-center">
                  <AttachmentPopover
                    attachmentKey={`house-rent-${index}`}
                    attachments={attachments}
                    onFileSelect={() =>
                      handleGenericFileSelect(
                        `house-rent-${index}`,
                        { house_rent_id: rent.house_rent_id },
                        rent.document_count
                      )
                    }
                    onAddMoreAttachments={handleAddMoreAttachments}
                    onRemoveAttachment={handleRemoveAttachment}
                    onOpenImage={openImageModal}
                    openPopoverKey={openPopoverKey}
                    setOpenPopoverKey={setOpenPopoverKey}
                    documentCount={rent.document_count}
                  />
                </div>
                <div style={{ width: "10%" }} className="flex justify-center">
                  <MdInsertComment
                    className="w-5 h-5 text-gray-400 cursor-pointer hover:text-blue-600 transition-colors"
                    onClick={() =>
                      handleGenericCommentClick(`house-rent-${index}`, {
                        house_rent_id: rent.house_rent_id,
                      })
                    }
                  />
                  {rent.comment_count > 0 && (
                    <span className="ml-1 text-xs text-gray-600">
                      {rent.comment_count}
                    </span>
                  )}
                </div>
                <div
                  style={{ width: "36%" }}
                  className="flex flex-col items-end"
                >
                  <div className="text-gray-900 text-[15px] font-medium">
                    {rent.actualAmount}
                  </div>
                  <span className="text-xs text-gray-400 font-normal">
                    ({rent.actualPerMonth})
                  </span>
                </div>
              </div>
              {/* Location and landlord details */}
              <div className="flex w-full px-6 py-4">
                <div
                  style={{ width: "28%" }}
                  className="flex flex-col gap-4 mt-2"
                >
                  <div className="text-gray-500 text-[13px]">
                    {rent.location}
                  </div>
                  <div className="text-[13px]">
                    <span className="font-medium text-[#7c82a1]">
                      Landlord Name
                    </span>
                    <span className="text-[#7c82a1]"> :</span>
                    <span className="text-gray-900"> {rent.landlordName}</span>
                  </div>
                  <div className="text-[13px]">
                    <span className="font-medium text-[#7c82a1]">PAN</span>
                    <span className="text-[#7c82a1]"> :</span>
                    <span className="text-gray-900"> {rent.landlordPan}</span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Other Income Section */}
      {declarationData?.other_source_income && (
        <div className="border-b border-gray-200 pb-4">
          <div className="text-[16px] font-semibold text-gray-900 px-6 pt-6 pb-2">
            Other Income
          </div>

          {/* Income from other sources */}
          {declarationData.other_source_income.income_from_other_sources &&
            parseFloat(
              declarationData.other_source_income.income_from_other_sources
            ) > 0 && (
              <div className="flex w-full px-6 py-2 items-center">
                <div
                  style={{ width: "28%" }}
                  className="text-gray-800 text-[15px]"
                >
                  Income from other sources
                </div>
                <div style={{ width: "16%" }} className="flex justify-end">
                  <div className="text-gray-900 text-[15px] font-medium">
                    ₹
                    {parseFloat(
                      declarationData.other_source_income
                        .income_from_other_sources
                    ).toLocaleString()}
                    
                  </div>
                </div>
                <div style={{ width: "10%" }} className="flex justify-center">
                  <AttachmentPopover
                    attachmentKey="other-income-income_from_other_sources"
                    attachments={attachments}
                    onFileSelect={() =>
                      handleGenericFileSelect(
                        "other-income-income_from_other_sources",
                        {
                          other_source_income_id:
                            declarationData.other_source_income
                              .other_source_income_id,
                          type: "income_from_other_sources",
                        },
                        declarationData.other_source_income.document_count
                          ?.income_from_other_sources || 0
                      )
                    }
                    onAddMoreAttachments={handleAddMoreAttachments}
                    onRemoveAttachment={handleRemoveAttachment}
                    onOpenImage={openImageModal}
                    openPopoverKey={openPopoverKey}
                    setOpenPopoverKey={setOpenPopoverKey}
                    documentCount={
                      declarationData.other_source_income.document_count
                        ?.income_from_other_sources || 0
                    }
                  />
                </div>
                <div style={{ width: "10%" }} className="flex justify-center">
                  <MdInsertComment
                    className="w-5 h-5 text-gray-400 cursor-pointer hover:text-blue-600 transition-colors"
                    onClick={() =>
                      handleGenericCommentClick(
                        "other-income-income_from_other_sources",
                        {
                          other_source_income_id:
                            declarationData.other_source_income
                              .other_source_income_id,
                          type: "income_from_other_sources",
                        }
                      )
                    }
                  />
                  {(declarationData.other_source_income.comment_count
                    ?.income_from_other_sources || 0) > 0 && (
                    <span className="ml-1 text-xs text-gray-600">
                      {
                        declarationData.other_source_income.comment_count
                          .income_from_other_sources
                      }
                    </span>
                  )}
                </div>
                <div
                  style={{ width: "36%" }}
                  className="flex items-center justify-end text-gray-900 text-[15px] font-medium"
                >
                  ₹
                  {parseFloat(
                    declarationData.other_source_income
                      .actual_income_from_other_sources || 0
                  ).toLocaleString()}
                  
                </div>
              </div>
            )}

          {/* Interest from savings */}
          {declarationData.other_source_income.interest_from_savings &&
            parseFloat(
              declarationData.other_source_income.interest_from_savings
            ) > 0 && (
              <div className="flex w-full px-6 py-2 items-center">
                <div
                  style={{ width: "28%" }}
                  className="text-gray-800 text-[15px]"
                >
                  Interest Earned from Savings Deposit
                </div>
                <div style={{ width: "16%" }} className="flex justify-end">
                  <div className="text-gray-900 text-[15px] font-medium">
                    ₹
                    {parseFloat(
                      declarationData.other_source_income.interest_from_savings
                    ).toLocaleString()}
                    
                  </div>
                </div>
                <div style={{ width: "10%" }} className="flex justify-center">
                  <AttachmentPopover
                    attachmentKey="other-income-interest_from_savings"
                    attachments={attachments}
                    onFileSelect={() =>
                      handleGenericFileSelect(
                        "other-income-interest_from_savings",
                        {
                          other_source_income_id:
                            declarationData.other_source_income
                              .other_source_income_id,
                          type: "interest_from_savings",
                        },
                        declarationData.other_source_income.document_count
                          ?.interest_from_savings || 0
                      )
                    }
                    onAddMoreAttachments={handleAddMoreAttachments}
                    onRemoveAttachment={handleRemoveAttachment}
                    onOpenImage={openImageModal}
                    openPopoverKey={openPopoverKey}
                    setOpenPopoverKey={setOpenPopoverKey}
                    documentCount={
                      declarationData.other_source_income.document_count
                        ?.interest_from_savings || 0
                    }
                  />
                </div>
                <div style={{ width: "10%" }} className="flex justify-center">
                  <MdInsertComment
                    className="w-5 h-5 text-gray-400 cursor-pointer hover:text-blue-600 transition-colors"
                    onClick={() =>
                      handleGenericCommentClick(
                        "other-income-interest_from_savings",
                        {
                          other_source_income_id:
                            declarationData.other_source_income
                              .other_source_income_id,
                          type: "interest_from_savings",
                        }
                      )
                    }
                  />
                  {(declarationData.other_source_income.comment_count
                    ?.interest_from_savings || 0) > 0 && (
                    <span className="ml-1 text-xs text-gray-600">
                      {
                        declarationData.other_source_income.comment_count
                          .interest_from_savings
                      }
                    </span>
                  )}
                </div>
                <div
                  style={{ width: "36%" }}
                  className="flex items-center justify-end text-gray-900 text-[15px] font-medium"
                >
                  ₹
                  {parseFloat(
                    declarationData.other_source_income
                      .actual_interest_from_savings || 0
                  ).toLocaleString()}
                  
                </div>
              </div>
            )}

          {/* Interest from fixed deposits */}
          {declarationData.other_source_income.interest_from_fixed_deposits &&
            parseFloat(
              declarationData.other_source_income.interest_from_fixed_deposits
            ) > 0 && (
              <div className="flex w-full px-6 py-2 items-center">
                <div
                  style={{ width: "28%" }}
                  className="text-gray-800 text-[15px]"
                >
                  Interest Earned from Fixed Deposit
                </div>
                <div style={{ width: "16%" }} className="flex justify-end">
                  <div className="text-gray-900 text-[15px] font-medium">
                    ₹
                    {parseFloat(
                      declarationData.other_source_income
                        .interest_from_fixed_deposits
                    ).toLocaleString()}
                    
                  </div>
                </div>
                <div style={{ width: "10%" }} className="flex justify-center">
                  <AttachmentPopover
                    attachmentKey="other-income-interest_from_fixed_deposits"
                    attachments={attachments}
                    onFileSelect={() =>
                      handleGenericFileSelect(
                        "other-income-interest_from_fixed_deposits",
                        {
                          other_source_income_id:
                            declarationData.other_source_income
                              .other_source_income_id,
                          type: "interest_from_fixed_deposits",
                        },
                        declarationData.other_source_income.document_count
                          ?.interest_from_fixed_deposits || 0
                      )
                    }
                    onAddMoreAttachments={handleAddMoreAttachments}
                    onRemoveAttachment={handleRemoveAttachment}
                    onOpenImage={openImageModal}
                    openPopoverKey={openPopoverKey}
                    setOpenPopoverKey={setOpenPopoverKey}
                    documentCount={
                      declarationData.other_source_income.document_count
                        ?.interest_from_fixed_deposits || 0
                    }
                  />
                </div>
                <div style={{ width: "10%" }} className="flex justify-center">
                  <MdInsertComment
                    className="w-5 h-5 text-gray-400 cursor-pointer hover:text-blue-600 transition-colors"
                    onClick={() =>
                      handleGenericCommentClick(
                        "other-income-interest_from_fixed_deposits",
                        {
                          other_source_income_id:
                            declarationData.other_source_income
                              .other_source_income_id,
                          type: "interest_from_fixed_deposits",
                        }
                      )
                    }
                  />
                  {(declarationData.other_source_income.comment_count
                    ?.interest_from_fixed_deposits || 0) > 0 && (
                    <span className="ml-1 text-xs text-gray-600">
                      {
                        declarationData.other_source_income.comment_count
                          .interest_from_fixed_deposits
                      }
                    </span>
                  )}
                </div>
                <div
                  style={{ width: "36%" }}
                  className="flex items-center justify-end text-gray-900 text-[15px] font-medium"
                >
                  ₹
                  {parseFloat(
                    declarationData.other_source_income
                      .actual_interest_from_fixed_deposits || 0
                  ).toLocaleString()}
                  
                </div>
              </div>
            )}

          {/* Interest from national savings */}
          {declarationData.other_source_income.interest_from_national_savings &&
            parseFloat(
              declarationData.other_source_income.interest_from_national_savings
            ) > 0 && (
              <div className="flex w-full px-6 py-2 items-center">
                <div
                  style={{ width: "28%" }}
                  className="text-gray-800 text-[15px]"
                >
                  Interest Earned from National Savings Certificates
                </div>
                <div style={{ width: "16%" }} className="flex justify-end">
                  <div className="text-gray-900 text-[15px] font-medium">
                    ₹
                    {parseFloat(
                      declarationData.other_source_income
                        .interest_from_national_savings
                    ).toLocaleString()}
                    
                  </div>
                </div>
                <div style={{ width: "10%" }} className="flex justify-center">
                  <AttachmentPopover
                    attachmentKey="other-income-interest_from_national_savings"
                    attachments={attachments}
                    onFileSelect={() =>
                      handleGenericFileSelect(
                        "other-income-interest_from_national_savings",
                        {
                          other_source_income_id:
                            declarationData.other_source_income
                              .other_source_income_id,
                          type: "interest_from_national_savings",
                        },
                        declarationData.other_source_income.document_count
                          ?.interest_from_national_savings || 0
                      )
                    }
                    onAddMoreAttachments={handleAddMoreAttachments}
                    onRemoveAttachment={handleRemoveAttachment}
                    onOpenImage={openImageModal}
                    openPopoverKey={openPopoverKey}
                    setOpenPopoverKey={setOpenPopoverKey}
                    documentCount={
                      declarationData.other_source_income.document_count
                        ?.interest_from_national_savings || 0
                    }
                  />
                </div>
                <div style={{ width: "10%" }} className="flex justify-center">
                  <MdInsertComment
                    className="w-5 h-5 text-gray-400 cursor-pointer hover:text-blue-600 transition-colors"
                    onClick={() =>
                      handleGenericCommentClick(
                        "other-income-interest_from_national_savings",
                        {
                          other_source_income_id:
                            declarationData.other_source_income
                              .other_source_income_id,
                          type: "interest_from_national_savings",
                        }
                      )
                    }
                  />
                  {(declarationData.other_source_income.comment_count
                    ?.interest_from_national_savings || 0) > 0 && (
                    <span className="ml-1 text-xs text-gray-600">
                      {
                        declarationData.other_source_income.comment_count
                          .interest_from_national_savings
                      }
                    </span>
                  )}
                </div>
                <div
                  style={{ width: "36%" }}
                  className="flex items-center justify-end text-gray-900 text-[15px] font-medium"
                >
                  ₹
                  {parseFloat(
                    declarationData.other_source_income
                      .actual_interest_from_national_savings || 0
                  ).toLocaleString()}
                  
                </div>
              </div>
            )}
        </div>
      )}

      {/* Investment Sections using the new component */}
      <InvestmentSection
        title="80C Investments"
        maxLimit="Not provided"
        investments={declarationData?.investments_80C}
        attachments={attachments}
        onFileSelect={handleGenericFileSelect}
        onAddMoreAttachments={handleAddMoreAttachments}
        onRemoveAttachment={handleRemoveAttachment}
        onOpenImage={openImageModal}
        openPopoverKey={openPopoverKey}
        setOpenPopoverKey={setOpenPopoverKey}
        onCommentClick={handleGenericCommentClick}
        keyPrefix="80c"
        getIdParams={(investment) => ({ emp_80c_id: investment.emp_80c_id })}
      />

      <InvestmentSection
        title="80D Exemptions"
        maxLimit="Not provided"
        investments={declarationData?.exemptions_80D}
        attachments={attachments}
        onFileSelect={handleGenericFileSelect}
        onAddMoreAttachments={handleAddMoreAttachments}
        onRemoveAttachment={handleRemoveAttachment}
        onOpenImage={openImageModal}
        openPopoverKey={openPopoverKey}
        setOpenPopoverKey={setOpenPopoverKey}
        onCommentClick={handleGenericCommentClick}
        keyPrefix="80d"
        getIdParams={(exemption) => ({ emp_80d_id: exemption.emp_80d_id })}
      />

      <InvestmentSection
        title="Other Investments & Exemptions"
        maxLimit="Not provided"
        investments={declarationData?.other_investments}
        attachments={attachments}
        onFileSelect={handleGenericFileSelect}
        onAddMoreAttachments={handleAddMoreAttachments}
        onRemoveAttachment={handleRemoveAttachment}
        onOpenImage={openImageModal}
        openPopoverKey={openPopoverKey}
        setOpenPopoverKey={setOpenPopoverKey}
        onCommentClick={handleGenericCommentClick}
        keyPrefix="other"
        getIdParams={(investment) => ({
          emp_other_investment_id: investment.emp_other_investment_id,
        })}
      />

      {/* Rental Income (moved here) */}
      {rentalIncomeArray.length > 0 && (
        <div className="border-b border-gray-200 pb-4">
          <div className="text-[16px] font-semibold text-gray-900 px-6 pt-6 pb-2">
            Net Income / Loss from House Property
          </div>
          <div className="flex w-full px-6 py-2 items-center">
            <div
              style={{ width: "28%" }}
              className="text-gray-700 text-[15px] font-medium"
            >
              Total Income / Loss from House Property
            </div>
            <div style={{ width: "16%" }} className="flex justify-end">
              <div className="text-gray-900 text-[15px] font-semibold">
                {displayedRentalTotal
                  ? `₹${displayedRentalTotal.toLocaleString()}`
                  : "₹0"}
              </div>
            </div>
            <div style={{ width: "10%" }} className="flex justify-center">
              <span className="text-gray-400">-</span>
            </div>
            <div style={{ width: "10%" }} className="flex justify-center">
              <span className="text-gray-400">-</span>
            </div>
            <div
              style={{ width: "36%" }}
              className="flex items-center justify-end text-gray-900 text-[15px] font-medium"
            >
              {displayedActualRentalTotal
                ? `₹${displayedActualRentalTotal.toLocaleString()}`
                : "₹0"}
            </div>
          </div>

          {regime === "old" && homeLoanInterestDisplay && (
              <div className="flex w-full px-6 py-2 items-center">
                <div
                  style={{ width: "28%" }}
                  className="text-gray-700 text-[15px]"
                >
                  Interest Paid on Home Loan
                  {declarationData?.home_loan?.name_of_lender && (
                    <div className="text-xs text-gray-500 mt-1">
                      Lender: {declarationData.home_loan.name_of_lender} &nbsp;
                      | &nbsp; PAN: {declarationData.home_loan.lender_pan}
                    </div>
                  )}
                </div>
                <div style={{ width: "16%" }} className="flex justify-end">
                  <div className="text-gray-900 text-[15px] font-medium">
                    {homeLoanInterestDisplay}
                  </div>
                </div>
                <div style={{ width: "10%" }} className="flex justify-center">
                  <AttachmentPopover
                    attachmentKey={`home-loan-interest-${declarationData.home_loan.home_loan_id}`}
                    attachments={attachments}
                    onFileSelect={() =>
                      handleGenericFileSelect(
                        `home-loan-interest-${declarationData.home_loan.home_loan_id}`,
                        {
                          home_loan_id: declarationData.home_loan.home_loan_id,
                          type: "interest_amount",
                        },
                        declarationData.home_loan.document_count?.interest_amount || 0
                      )
                    }
                    onAddMoreAttachments={handleAddMoreAttachments}
                    onRemoveAttachment={handleRemoveAttachment}
                    onOpenImage={openImageModal}
                    openPopoverKey={openPopoverKey}
                    setOpenPopoverKey={setOpenPopoverKey}
                    documentCount={declarationData.home_loan.document_count?.interest_amount || 0}
                  />
                </div>
                <div style={{ width: "10%" }} className="flex justify-center">
                  <MdInsertComment
                    className="w-5 h-5 text-gray-400 cursor-pointer hover:text-blue-600 transition-colors"
                    onClick={() =>
                      handleGenericCommentClick(
                        `home-loan-interest-${declarationData.home_loan.home_loan_id}`,
                        {
                          home_loan_id: declarationData.home_loan.home_loan_id,
                          type: "interest_amount",
                        }
                      )
                    }
                  />
                  {(declarationData.home_loan.comment_count?.interest_amount || 0) > 0 && (
                    <span className="ml-1 text-xs text-gray-600">
                      {declarationData.home_loan.comment_count.interest_amount}
                    </span>
                  )}
                </div>
                <div
                  style={{ width: "36%" }}
                  className="flex items-center justify-end text-gray-900 text-[15px] font-medium"
                >
                  {actualHomeLoanInterestDisplay || "₹0"}
                </div>
              </div>
            )}

          {rentalIncomeArray.map((it, idx) => {
            const declaredAnnualRent = parseFloat(it.annual_rent_received || 0);
            const declaredMunicipalTaxes = parseFloat(it.municipal_taxes_paid || 0);
            const declaredNetAnnualValue = parseFloat(it.net_annual_value || 0);
            const declaredStandardDeduction = parseFloat(it.standard_deduction || 0);
            const declaredInterest = parseFloat(it.interest_amount || 0);
            
            // Calculate actual values
            const actualAnnualRent = parseFloat(it.actual_annual_rent_received || 0);
            const actualMunicipalTaxes = parseFloat(it.actual_municipal_taxes_paid || 0);
            const actualNetAnnualValue = actualAnnualRent - actualMunicipalTaxes;
            const actualStandardDeduction = actualNetAnnualValue * 0.3;
            const actualInterest = parseFloat(it.actual_interest_amount || 0);
            
            // Calculate actual net income/loss based on regime
            let actualNetIncomeLoss;
            if (regime === "old") {
              actualNetIncomeLoss = actualNetAnnualValue - actualStandardDeduction - actualInterest;
            } else {
              actualNetIncomeLoss = actualNetAnnualValue - actualStandardDeduction;
            }

            const item = {
              title: `Net Income / Loss from Let Out Property - ${idx + 1}`,
              annual_rent_received: declaredAnnualRent,
              municipal_taxes_paid: declaredMunicipalTaxes,
              net_annual_value: declaredNetAnnualValue,
              standard_deduction: declaredStandardDeduction,
              repaying_home_loan: it.repaying_home_loan === "1" || it.repaying_home_loan === true,
              repaying_home_loan_data: {
                interest_amount: declaredInterest,
                name_of_lender: it.name_of_lender || "",
                lender_pan: it.lender_pan || "",
              },
              net_income_loss: parseFloat(it.net_income_loss || 0),
              amountDisplay: `₹${parseFloat(it.net_income_loss || 0).toLocaleString()}`,
              actualAmountDisplay: `₹${actualNetIncomeLoss.toLocaleString()}`,
            };

            return (
              <div
                key={it.rental_income_id || idx}
                className="flex flex-col border-t border-gray-100"
              >
                <div className="flex w-full px-6 py-3 items-center">
                  <div
                    style={{ width: "28%" }}
                    className="flex items-center gap-3"
                  >
                    <button
                      type="button"
                      onClick={() => toggleRentalRow(idx)}
                      className="flex items-center gap-2 text-sm text-gray-700"
                      aria-expanded={!!openRentalRows[idx]}
                    >
                      {openRentalRows[idx] ? (
                        <MdRemoveCircle className="text-red-500 w-5 h-5" />
                      ) : (
                        <MdAddCircle className="text-blue-500 w-5 h-5" />
                      )}
                      <span className="font-medium text-gray-900">
                        {item.title}
                      </span>
                    </button>
                  </div>
                  <div style={{ width: "16%" }} className="flex justify-end">
                    <div className="text-gray-900 text-[15px] font-medium">
                      {item.amountDisplay}
                    </div>
                  </div>
                  <div style={{ width: "10%" }} className="flex justify-center">
                    <span className="text-gray-400">-</span>
                  </div>
                  <div style={{ width: "10%" }} className="flex justify-center">
                    <span className="text-gray-400">-</span>
                  </div>
                  <div
                    style={{ width: "36%" }}
                    className="flex items-center justify-end text-gray-900 text-[15px] font-medium"
                  >
                    {item.actualAmountDisplay}
                  </div>
                </div>

                {openRentalRows[idx] && (
                  <div className="pl-6 pr-6 pb-4 border-l-2 border-gray-100">
                    <div className="space-y-2 text-sm text-gray-700">
                      {/* Annual Rent Received */}
                      <div className="flex w-full items-center">
                        <div style={{ width: "28%" }}>Annual Rent Received</div>
                        <div style={{ width: "16%" }} className="flex justify-end text-gray-900 font-medium">
                          ₹{item.annual_rent_received.toLocaleString()}
                        </div>
                        <div style={{ width: "10%" }} className="flex justify-center">
                          <AttachmentPopover
                            attachmentKey={`rental-annual-rent-${it.rental_income_id}`}
                            attachments={attachments}
                            onFileSelect={() =>
                              handleGenericFileSelect(
                                `rental-annual-rent-${it.rental_income_id}`,
                                {
                                  rental_income_id: it.rental_income_id,
                                  type: "annual_rent_received",
                                },
                                it.document_count?.annual_rent_received || 0
                              )
                            }
                            onAddMoreAttachments={handleAddMoreAttachments}
                            onRemoveAttachment={handleRemoveAttachment}
                            onOpenImage={openImageModal}
                            openPopoverKey={openPopoverKey}
                            setOpenPopoverKey={setOpenPopoverKey}
                            documentCount={it.document_count?.annual_rent_received || 0}
                          />
                        </div>
                        <div style={{ width: "10%" }} className="flex justify-center">
                          <MdInsertComment
                            className="w-5 h-5 text-gray-400 cursor-pointer hover:text-blue-600 transition-colors"
                            onClick={() =>
                              handleGenericCommentClick(`rental-annual-rent-${it.rental_income_id}`, {
                                rental_income_id: it.rental_income_id,
                                type: "annual_rent_received",
                              })
                            }
                          />
                          {(it.comment_count?.annual_rent_received || 0) > 0 && (
                            <span className="ml-1 text-xs text-gray-600">
                              {it.comment_count.annual_rent_received}
                            </span>
                          )}
                        </div>
                        <div style={{ width: "36%" }} className="flex justify-end text-gray-900">
                          ₹{parseFloat(it.actual_annual_rent_received || 0).toLocaleString()}
                        </div>
                      </div>

                      {/* Municipal Taxes Paid */}
                      <div className="flex w-full items-center">
                        <div style={{ width: "28%" }}>Municipal Taxes Paid</div>
                        <div style={{ width: "16%" }} className="flex justify-end text-gray-900 font-medium">
                          ₹{item.municipal_taxes_paid.toLocaleString()}
                        </div>
                        <div style={{ width: "10%" }} className="flex justify-center">
                          <AttachmentPopover
                            attachmentKey={`rental-municipal-taxes-${it.rental_income_id}`}
                            attachments={attachments}
                            onFileSelect={() =>
                              handleGenericFileSelect(
                                `rental-municipal-taxes-${it.rental_income_id}`,
                                {
                                  rental_income_id: it.rental_income_id,
                                  type: "municipal_taxes_paid",
                                },
                                it.document_count?.municipal_taxes_paid || 0
                              )
                            }
                            onAddMoreAttachments={handleAddMoreAttachments}
                            onRemoveAttachment={handleRemoveAttachment}
                            onOpenImage={openImageModal}
                            openPopoverKey={openPopoverKey}
                            setOpenPopoverKey={setOpenPopoverKey}
                            documentCount={it.document_count?.municipal_taxes_paid || 0}
                          />
                        </div>
                        <div style={{ width: "10%" }} className="flex justify-center">
                          <MdInsertComment
                            className="w-5 h-5 text-gray-400 cursor-pointer hover:text-blue-600 transition-colors"
                            onClick={() =>
                              handleGenericCommentClick(`rental-municipal-taxes-${it.rental_income_id}`, {
                                rental_income_id: it.rental_income_id,
                                type: "municipal_taxes_paid",
                              })
                            }
                          />
                          {(it.comment_count?.municipal_taxes_paid || 0) > 0 && (
                            <span className="ml-1 text-xs text-gray-600">
                              {it.comment_count.municipal_taxes_paid}
                            </span>
                          )}
                        </div>
                        <div style={{ width: "36%" }} className="flex justify-end text-gray-900">
                          ₹{parseFloat(it.actual_municipal_taxes_paid || 0).toLocaleString()}
                        </div>
                      </div>

                      {/* Net Annual Value - calculate actual based on actual inputs */}
                      <div className="flex w-full items-center">
                        <div style={{ width: "28%" }}>Net Annual Value</div>
                        <div style={{ width: "16%" }} className="flex justify-end text-gray-900 font-medium">
                          ₹{item.net_annual_value.toLocaleString()}
                        </div>
                        <div style={{ width: "10%" }} className="flex justify-center text-gray-400">-</div>
                        <div style={{ width: "10%" }} className="flex justify-center text-gray-400">-</div>
                        <div style={{ width: "36%" }} className="flex justify-end text-gray-900">
                          ₹{actualNetAnnualValue.toLocaleString()}
                        </div>
                      </div>

                      {/* Standard Deduction - calculate actual based on actual net annual value */}
                      <div className="flex w-full items-center">
                        <div style={{ width: "28%" }}>Standard Deduction (@ 30% of Net Annual Value)</div>
                        <div style={{ width: "16%" }} className="flex justify-end text-gray-900 font-medium">
                          ₹{item.standard_deduction.toLocaleString()}
                        </div>
                        <div style={{ width: "10%" }} className="flex justify-center text-gray-400">-</div>
                        <div style={{ width: "10%" }} className="flex justify-center text-gray-400">-</div>
                        <div style={{ width: "36%" }} className="flex justify-end text-gray-900">
                          ₹{actualStandardDeduction.toLocaleString()}
                        </div>
                      </div>

                      {/* Interest Paid on Home Loan (if applicable and old regime) */}
                      {item.repaying_home_loan && regime === "old" && (
                        <>
                          <div className="flex w-full items-center">
                            <div style={{ width: "28%" }}>Interest Paid on Home Loan</div>
                            <div style={{ width: "16%" }} className="flex justify-end text-gray-900 font-medium">
                              ₹{(item.repaying_home_loan_data.interest_amount || 0).toLocaleString()}
                            </div>
                            <div style={{ width: "10%" }} className="flex justify-center">
                              <AttachmentPopover
                                attachmentKey={`rental-interest-${it.rental_income_id}`}
                                attachments={attachments}
                                onFileSelect={() =>
                                  handleGenericFileSelect(
                                    `rental-interest-${it.rental_income_id}`,
                                    {
                                      rental_income_id: it.rental_income_id,
                                      type: "interest_amount",
                                    },
                                    it.document_count?.interest_amount || 0
                                  )
                                }
                                onAddMoreAttachments={handleAddMoreAttachments}
                                onRemoveAttachment={handleRemoveAttachment}
                                onOpenImage={openImageModal}
                                openPopoverKey={openPopoverKey}
                                setOpenPopoverKey={setOpenPopoverKey}
                                documentCount={it.document_count?.interest_amount || 0}
                              />
                            </div>
                            <div style={{ width: "10%" }} className="flex justify-center">
                              <MdInsertComment
                                className="w-5 h-5 text-gray-400 cursor-pointer hover:text-blue-600 transition-colors"
                                onClick={() =>
                                  handleGenericCommentClick(`rental-interest-${it.rental_income_id}`, {
                                    rental_income_id: it.rental_income_id,
                                    type: "interest_amount",
                                  })
                                }
                              />
                              {(it.comment_count?.interest_amount || 0) > 0 && (
                                <span className="ml-1 text-xs text-gray-600">
                                  {it.comment_count.interest_amount}
                                </span>
                              )}
                            </div>
                            <div style={{ width: "36%" }} className="flex justify-end text-gray-900">
                              ₹{parseFloat(it.actual_interest_amount || 0).toLocaleString()}
                            </div>
                          </div>
                          {/* Lender details left-aligned under the details (not centered) */}
                          <div className="text-xs text-gray-500 mt-1">
                            Lender: {item.repaying_home_loan_data.name_of_lender || "-"} &nbsp; | &nbsp; PAN: {item.repaying_home_loan_data.lender_pan || "-"}
                          </div>
                        </>
                      )}

                      {/* Show interest for new regime but indicate it's not deducted */}
                      {item.repaying_home_loan && regime === "new" && (
                        <>
                          <div className="flex w-full items-center">
                            <div style={{ width: "28%" }}>
                              Interest Paid on Home Loan
                            </div>
                            <div style={{ width: "16%" }} className="flex justify-end text-gray-900 font-medium">
                              ₹{(item.repaying_home_loan_data.interest_amount || 0).toLocaleString()}
                            </div>
                            <div style={{ width: "10%" }} className="flex justify-center">
                              <AttachmentPopover
                                attachmentKey={`rental-interest-${it.rental_income_id}`}
                                attachments={attachments}
                                onFileSelect={() =>
                                  handleGenericFileSelect(
                                    `rental-interest-${it.rental_income_id}`,
                                    {
                                      rental_income_id: it.rental_income_id,
                                      type: "interest_amount",
                                    },
                                    it.document_count?.interest_amount || 0
                                  )
                                }
                                onAddMoreAttachments={handleAddMoreAttachments}
                                onRemoveAttachment={handleRemoveAttachment}
                                onOpenImage={openImageModal}
                                openPopoverKey={openPopoverKey}
                                setOpenPopoverKey={setOpenPopoverKey}
                                documentCount={it.document_count?.interest_amount || 0}
                              />
                            </div>
                            <div style={{ width: "10%" }} className="flex justify-center">
                              <MdInsertComment
                                className="w-5 h-5 text-gray-400 cursor-pointer hover:text-blue-600 transition-colors"
                                onClick={() =>
                                  handleGenericCommentClick(`rental-interest-${it.rental_income_id}`, {
                                    rental_income_id: it.rental_income_id,
                                    type: "interest_amount",
                                  })
                                }
                              />
                              {(it.comment_count?.interest_amount || 0) > 0 && (
                                <span className="ml-1 text-xs text-gray-600">
                                  {it.comment_count.interest_amount}
                                </span>
                              )}
                            </div>
                            <div style={{ width: "36%" }} className="flex justify-end text-gray-900">
                              ₹{parseFloat(it.actual_interest_amount || 0).toLocaleString()}
                            </div>
                          </div>
                          {/* Lender details left-aligned under the details (not centered) */}
                          <div className="text-xs text-gray-500 mt-1">
                            Lender: {item.repaying_home_loan_data.name_of_lender || "-"} &nbsp; | &nbsp; PAN: {item.repaying_home_loan_data.lender_pan || "-"}
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ProofOfInvestments;
